<template>
  <vuetify-textarea
    v-bind="$attrs"
    v-on="$listeners"
    :value="localValue"
    @input="onInput"
    class="base-textarea"
    :class="dynamicClasses"
    ref="baseTextarea"
    outlined
    :persistent-placeholder="true"
  >
    <template #label>
      {{ label }}
    </template>
    <template v-for="(slotContent, slotName) in slots" v-slot:[slotName]>
      <slot :name="slotName">{{ slotContent(slotProps) }}</slot>
    </template>
  </vuetify-textarea>
</template>
<script>
import { VTextarea } from 'vuetify/lib/components'

export default {
  name: 'BaseTextarea',
  components: {
    'vuetify-textarea': VTextarea,
  },
  data() {
    return {
      localValue: this.value ?? '',
    }
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['medium', 'small'].includes(value),
    },
    label: {
      type: String,
      default: '',
    },
  },
  updated() {
    this.handleInputLength()
    this.loadMaxLengthCounter()
  },
  methods: {
    onInput(val) {
      this.localValue = val
      this.$emit('input', val)
    },
    loadMaxLengthCounter() {
      if (!this.$refs.baseTextarea || !this.$refs.baseTextarea.$el) {
        return
      }

      const counterEl = this.$refs.baseTextarea.$el.querySelector('.v-counter')
      if (counterEl) {
        const counterString = counterEl.innerHTML.split('/')
        let inputLength = parseInt(counterString[1], 10)
        if (inputLength && inputLength > 999) {
          const formattedNumber = inputLength.toLocaleString('pt-BR')
          counterString[1] = ' ' + formattedNumber
          counterEl.innerHTML = counterString.join('/')
        }
      }
    },
    handleInputLength() {
      const inputLength = this.localValue ? this.localValue.length : 0
      const counterEl = this.$refs.baseTextarea.$el.querySelector('.v-counter')

      if (counterEl) {
        const counterString = counterEl.innerHTML.split('/')
        const formattedNumber =
          inputLength > 999
            ? inputLength.toLocaleString('pt-BR')
            : inputLength.toString()

        counterString[0] = formattedNumber + ' '
        counterEl.innerHTML = counterString.join('/')
      }
    },
  },
  mounted() {
    this.loadMaxLengthCounter()
  },
  computed: {
    slots() {
      if (this.$slots.hasOwnProperty('label')) delete this.$slots.label
      return this.$slots
    },
    isFilled() {
      return Boolean(this.localValue && this.localValue.length)
    },
    dynamicClasses() {
      const returnObj = {
        filled: this.isFilled,
      }
      returnObj[`size--${this.size}`] = true

      return returnObj
    },
  },
  watch: {
    value(val) {
      this.localValue = val
      this.handleInputLength()
    },
    localValue(val) {
      this.$emit('input', val)
    },
  },
}
</script>

<style lang="scss" scoped>
.base-textarea.v-input {
  &::v-deep {
    textarea {
      font-weight: 400;
      font-size: 14px;
      &::placeholder {
        font-weight: 400;
        font-size: 14px;
        color: varFigma(neutral-60) !important;
      }
      @media only screen and (max-width: 960px) {
        font-size: 16px;
        &::placeholder {
          font-size: 16px;
        }
      }
    }

    .v-text-field__details {
      padding: 0 !important;
    }

    .v-text-field__slot {
      .v-label {
        top: -20px;
        font-size: 12px;
        font-weight: 500;
        color: varFigma(neutral-40-light);
        &--active {
          transform: none !important;
        }
      }
    }

    .v-messages {
      color: varFigma(neutral-70) !important;
    }

    .v-counter {
      color: varFigma(neutral-70) !important;
    }

    fieldset {
      padding-left: 0 !important;
      top: 0px;
      border: 1px solid varFigma(neutral-95-lightest);

      legend {
        display: none !important;
      }
    }

    &.filled {
      .v-text-field__slot {
        .v-label {
          color: varFigma(neutral-20-dark) !important;
        }
      }

      textarea {
        color: varFigma(neutral-40-light) !important;
      }

      .v-counter {
        color: varFigma(neutral-50) !important;
      }

      .v-messages {
        color: varFigma(neutral-50) !important;
      }

      &.v-input--is-focused {
        textarea {
          color: varFigma(neutral-30-base) !important;
        }
      }

      &:hover {
        textarea {
          color: varFigma(neutral-30-base) !important;
        }
      }
    }

    &.v-input--is-focused {
      fieldset {
        border: 2px solid varFigma(brand-60-base) !important;
      }

      .v-counter {
        color: varFigma(neutral-40-light) !important;
      }
      
      .v-messages {
        color: varFigma(neutral-40-light) !important;
      }
    }

    &:hover {
      .v-label {
        color: varFigma(neutral-20-dark);
      }

      fieldset {
        border: 1px solid varFigma(brand-60-base);
      }

      .v-counter {
        color: varFigma(neutral-50) !important;
      }

      .v-messages {
        color: varFigma(neutral-50) !important;
      }
    }

    &.error--text {
      .v-text-field__slot {
        .v-label {
          color: varFigma(feedback-danger) !important;
        }
      }

      fieldset {
        border: 1px solid varFigma(feedback-danger-light) !important;
        background-color: varFigma(feedback-danger-background) !important;
      }

      .v-text-field__slot textarea {
        color: varFigma(feedback-danger-dark) !important;
      }

      .v-counter {
        color: varFigma(feedback-danger) !important;
      }

      .v-messages {
        color: varFigma(feedback-danger) !important;
      }
    }

    &.v-input--is-disabled {
      .v-text-field__slot {
        .v-label {
          color: varFigma(neutral-70) !important;
        }
      }

      .v-text-field__slot textarea {
        color: varFigma(neutral-70) !important;
      }

      fieldset {
        border: 1px solid varFigma(neutral-80-lighter) !important;
        background-color: varFigma(neutral-95-lightest) !important;
      }

      .v-counter {
        display: none !important;
      }

      .v-messages {
        display: none !important;
      }
    }

    &.v-input--is-readonly {
      .v-text-field__slot {
        .v-label {
          color: varFigma(neutral-40-light) !important;
          left: 0px !important;
          top: -8px !important;
        }
      }
      .v-text-field__slot textarea {
        color: varFigma(neutral-60) !important;
        &::placeholder {
          color: varFigma(neutral-60) !important;
        }
      }
      &.filled {
        .v-text-field__slot {
          .v-label {
            color: varFigma(neutral-20-dark) !important;
          }
        }
        .v-text-field__slot textarea {
          color: varFigma(neutral-60) !important;
        }
      }

      .v-counter {
        display: none !important;
      }

      .v-messages {
        display: none !important;
      }
    }

    &.size {
      &--small {
        textarea {
          font-size: 12px;
          &::placeholder {
            font-size: 12px;
          }
        }
      }
    }
  }
}
</style>
